import { Injectable, Injector, TemplateRef, ViewChild, isDevMode } from '@angular/core';
import { CoreModalService } from '@app/core/services/core-modal.service';
import { IEidosModalChooseDateRange, IEidosModalConfig, IEidosModalInfoConfirm, IEidosModalInfoData, IEidosModalType } from './eidos-modal.component';
import { BehaviorSubject } from 'rxjs';
import uniqueId from 'lodash/uniqueId';
import validationEngine from 'devextreme/ui/validation_engine';
import { DomSanitizer } from '@angular/platform-browser';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class EidosModalService {

  openModals: BehaviorSubject<IEidosModalConfig[]> = new BehaviorSubject<IEidosModalConfig[]>([]);

  @ViewChild('iframeTemplate') iframeTemplate!: TemplateRef<any>;

  constructor(private sanitizer: DomSanitizer, private injector: Injector) {
    window.addEventListener("message", (event) => {
      if(isDevMode() && event?.origin !== window.origin) {
        if(!!event.data) this.actionFrame(event?.data);
      } else if(!isDevMode() && event?.origin === window.origin) {
        if(!!event.data) this.actionFrame(event?.data);
      }

    }, false);
  }

  actionFrame(cmd:any) {
    switch(cmd?.action?.toLowerCase()) {
      case 'close':
        this.closeCurrentModal();
        break;
      case 'titlepopup':
        this.updateCurrentTitle(cmd?.data);
        break;
    }
  }
  updateCurrentTitle(title:string) {
    const l = this.openModals.value.length;
    if(l<1) return;
    const cfg = this.openModals.value[l-1]
    cfg.title = () => title;
  }
  openModal(modalConfig: IEidosModalConfig, _isOpenPromoCard?: boolean): number {
    const aboveCoreModalClass = 'eidos-modal-above-core';
    const cssClasses = (modalConfig.cssClass ?? '').split(' ').filter(c => c && c !== aboveCoreModalClass);
    if (this.injector.get(CoreModalService).hasOpenModals) cssClasses.push(aboveCoreModalClass);
    modalConfig.cssClass = cssClasses.join(' ');
    modalConfig.modalID = Math.max(...[0, ...this.openModals.value.map((modal: IEidosModalConfig) => modal.modalID!)]) + 1;
    modalConfig.open = true;
    this.openModals.next([...this.openModals.value, modalConfig]);

    return modalConfig.modalID;
  }

  openModalPromise(modalConfig: IEidosModalConfig): Promise<any> {
    modalConfig.modalID = Math.max(...[0, ...this.openModals.value.map((modal: IEidosModalConfig) => modal.modalID!)]) + 1;
    modalConfig.open = true;
    const onclose = modalConfig.onClosed ?? ((_e: any) => { });
    const self = this;
    return new Promise<boolean>(resolve => {
      modalConfig.onClosed = (event) => {
        resolve(event)
        onclose(event);
      },
        self.openModal(modalConfig);
    }).then(choose => choose);

  }
  // called automatically when the modal opened property is set to false
  closeModalByID(modalID: number) {
    const modal = this.openModals.value.find((modal: IEidosModalConfig) => modal.modalID === modalID);
    if (!modal) return
    const index = this.openModals.value.indexOf(modal)

    // remove all modals after the one we want to close because i use a stack logic
    !modal.doNotUseStackLogic && this.openModals.next(this.openModals.value.slice(0, index));
  }

  // to call if you want to close a modal from outside the modal component
  closeModal(modalKey: string) {
    if (!modalKey) return;
    const modal = this.openModals.value.find((modal: IEidosModalConfig) => modal.modalKey === modalKey);
    if (modal) modal.open = false;
  }
  closeCurrentModal() {
    const l = this.openModals.value.length;
    if(l<1) return;
    this.closeModal(this.openModals.value[l-1].modalKey)
  }
  isModalOpen(modalKey: string): boolean {
    return this.openModals.value.some((modal: IEidosModalConfig) => modal.modalKey === modalKey && modal.open);
  }

  applyModalValidation(modalConfig: IEidosModalConfig): boolean {
    return !modalConfig.formFields?.length || !!validationEngine.validateGroup(modalConfig.modalKey).isValid;
  }

  infoMessage(data: IEidosModalInfoData): Promise<void> {
    return new Promise<void>(resolve => {
      const modalConfig: IEidosModalConfig = {
        modalKey: uniqueId('modalEidosInfoDialog'),
        type: IEidosModalType.Basic,
        width: data.width ?? '80%',
        height: data.height ?? '100%',
        position: data.position ?? 'center',
        hideOnOutsideClick: false,
        doNotUseStackLogic: true,
        title: () => data.title,
        subtitle: () => data.subtitle ?? '',
        body: () => data.message ?? '',
        onClosed: () => resolve(),
        cssClass: data.cssClass ?? 'modal-eidos-dialog modal-eidos-dialog-info',
      }
      this.openModal(modalConfig);
    });
  }
  async warningMessage(data: IEidosModalInfoData): Promise<void> {
    return new Promise<void>(resolve => {
      const modalConfig: IEidosModalConfig = {
        modalKey: uniqueId('modalEidosWarningDialog'),
        type: IEidosModalType.Basic,
        width: data.width ?? '80%',
        height: data.height ?? '100%',
        position: data.position ?? 'center',
        hideOnOutsideClick: false,
        doNotUseStackLogic: true,
        title: () => data.title,
        subtitle: () => data.subtitle ?? '',
        body: () => data.message ?? '',
        onClosed: () => resolve(),
        cssClass: 'modal-eidos-dialog modal-eidos-dialog-warning',
      }
      this.openModal(modalConfig);
    });
  }
  errorMessage(data: IEidosModalInfoData): Promise<void> {
    return new Promise<void>(resolve => {
      const modalConfig: IEidosModalConfig = {
        modalKey: uniqueId('modalEidosErrorDialog'),
        type: IEidosModalType.Basic,
        width: data.width ?? '80%',
        height: data.height ?? '100%',
        position: data.position ?? 'center',
        hideOnOutsideClick: false,
        doNotUseStackLogic: true,
        title: () => data.title,
        subtitle: () => data.subtitle ?? '',
        body: () => data.message ?? '',
        onClosed: () => resolve(),
        cssClass: 'modal-eidos-dialog modal-eidos-dialog-error',
      }
      this.openModal(modalConfig);
    });
  }
  confirmMessage(data: IEidosModalInfoConfirm): Promise<boolean> {
    const self = this;
    return new Promise<boolean>(resolve => {
      const modalConfig: IEidosModalConfig = {
        modalKey: uniqueId('modalEidosConfirmDialog'),
        type: IEidosModalType.Basic,
        width: data.width ?? '60%',
        height: data.height ?? '70%',
        position: data.position ?? 'center',
        doNotUseStackLogic: true,
        title: () => data.title,
        subtitle: () => data.subtitle ?? '',
        body: () => data.message ?? '',
        hideOnOutsideClick: false,
        cssClass: 'modal-eidos-dialog modal-eidos-dialog-confirm',
        buttonsConfig: [
          {
            text: () => data.captionCancel ?? 'CANCEL',
            onClick: () => {
              self.closeModal(modalConfig.modalKey);
              resolve(false);
            },
            position: 'center',
            stylingMode: 'contained-danger',
          },
          {
            text: () => data.captionOk ?? 'OK',
            onClick: () => {
              self.closeModal(modalConfig.modalKey);
              resolve(true);
            },
            position: 'center',
            stylingMode: 'contained-success',
          },

        ],
      }
      self.openModal(modalConfig);
    }).then(choose => choose);

  }
  chooseDateRange(data: IEidosModalChooseDateRange): Promise<any> {
    const self = this;
    return new Promise<boolean>(resolve => {
      const modalConfig: IEidosModalConfig = {
        modalKey: uniqueId('modalEidosChooseRangeDialog'),
        type: IEidosModalType.Basic,
        width: data.width ?? '60%',
        height: data.height ?? '400px',
        position: data.position ?? 'center',
        title: () => data.title,
        subtitle: () => data.subtitle ?? '',
        formFields: [
          {
            field: 'from',
            caption: 'Date From',
            type: 'date',
            placeholder: 'Date From',
            width:'200px',
            required: true,
            showClearButton: false,
            disabledDates: (args: { date: Date, component: any, view: string }) => {
              return data.start && data.start.isValid ? data.start.valueOf() > args.date.valueOf() : false;
            },
            onValueChange: (value:any, _fields:any, values: any) => {
              const dtFrom = value as DateTime
              if (!dtFrom) return;
              const dtTo = values['to'] as DateTime
              if (!dtTo) return;
              if(dtFrom.startOf('day').valueOf()<=dtTo.startOf('day').valueOf()) return
              values['to'] = DateTime.fromJSDate(dtFrom.toJSDate()).startOf('day')
            },
            handleValueChange: true,
          },
          {
            field: 'to',
            caption: 'Date To',
            type: 'date',
            placeholder: 'Date To',
            width:'200px',
            required: true,
            showClearButton: false,
            disabledDates: (args: { date: Date, component: any, view: string }) => {
              return data.end && data.end.isValid ? data.end.valueOf() < args.date.valueOf() : false;
            },
            onValueChange: (value:any, _fields:any, values: any) => {
              const dtTo = value as DateTime
              if (!dtTo) return;
              const dtFrom = values['from'] as DateTime
              if (!dtFrom) return;
              if(dtFrom.startOf('day').valueOf()<=dtTo.startOf('day').valueOf()) return
              values['from'] = DateTime.fromJSDate(dtTo.toJSDate()).startOf('day')
            },
            handleValueChange: true,
          },
        ],
        formValues: { ...data },
        hideOnOutsideClick: false,
        cssClass: 'modal-eidos-dialog modal-eidos-dialog-choose-dates',
        buttonsConfig: [
          {
            text: () => data.captionCancel ?? 'CANCEL',
            onClick: () => {
              self.closeModal(modalConfig.modalKey);
              resolve(false);
            },
            position: 'center',
            stylingMode: 'contained-danger',
          },
          {
            text: () => data.captionOk ?? 'CONFIRM',
            onClick: () => {
              if (!self.applyModalValidation(modalConfig)) return

              self.closeModal(modalConfig.modalKey);
              resolve(modalConfig.formValues);
            },
            position: 'center',
            stylingMode: 'contained-success',
          },

        ],
      }
      self.openModal(modalConfig);
    }).then(choose => choose);

  }
  openIframe(data: IEidosModalInfoData): Promise<void> {
    return new Promise<void>(resolve => {
      const modalConfig: IEidosModalConfig = {
        modalKey: data.modalKey ?? uniqueId('modalEidosIframeDialog'),
        type: IEidosModalType.Basic,
        width: data.width ?? '80%',
        height: data.height ?? '100%',
        position: data.position ?? 'right',
        hideOnOutsideClick: false,
        doNotUseStackLogic: true,
        iframeUrl: data.iframeUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(data.iframeUrl) : '',
        title: () => data.title,
        onClosed: () => {
          data.closeCallback?.();
          resolve()
        },
        buttonsConfig: [
          {
            text: () => 'CANCEL',
            onClick: () => this.closeModal(modalConfig.modalKey),
            position: 'left',
            stylingMode: 'contained-danger',
          },
        ]
      }
      this.openModal(modalConfig);
    })
  }
}
